import React from 'react';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-react';
import { LocationCell } from './LocationCell';
import { useTranslation } from 'react-i18next';
import { alignBookingToRoute } from '@/components/trip/tripUtils';
import { getTypeCode } from '@/components/booking-grid/helpers';

interface BookingDetailsTableProps {
  trip: any;
  getLocationColor: (loc: string) => string | null;
  getCompanyShortName: (name: string) => string;
  /** Samme forkorter som turraden over — gir både visning og stoppidentitet (kolonneplassering). */
  getLocationShortName?: (loc: string) => string;
}

/** Iter 856: undertabellen «Tilknyttede bookinger» viser bookingens stopp i TURENS kolonner (St1..StN / T1..TN som turraden
 *  over, ikke fast St1–St3/T1–T2) + bookingens Type-kode. Stoppidentitet = turradens forkortelse (Forkortelser-registeret). */
export function BookingDetailsTable({ trip, getLocationColor, getCompanyShortName, getLocationShortName }: BookingDetailsTableProps) {
  const { t } = useTranslation();
  const bookings: any[] = trip.bookings || [];
  const shortName = getLocationShortName || ((l: string) => l);
  const keyFn = (loc: string) => String(shortName(loc) || loc || '').toLowerCase().trim();
  const routeStops = [{ location: trip.from_location }, ...(trip.stops || []), { location: trip.to_location }];
  // Iter 813: legacy `off_route`-stopp (utenfor turens lagrede rute) vises ikke
  const storedStops = (b: any) => (b.stops || []).filter((s: any) => s && s.off_route !== true);
  const displayStops = (b: any) => {
    const r = alignBookingToRoute(b, routeStops, keyFn, storedStops(b));
    // Vis turens egen etikett i kolonnen (identisk med turraden over) når stoppet er plassert.
    if (r.aligned) r.stops = r.stops.map((s: any, i: number) => (s.unused ? s : { ...s, location: trip.stops?.[i]?.location || s.location }));
    return r;
  };
  const aligned = bookings.map((b) => displayStops(b));
  const stopCount = Math.max(trip.stops?.length || 0, ...aligned.map((a) => a.stops.length));

  return (
    <div className="text-xs" data-testid={`weekly-bookings-${trip.trip_number}`}>
      <div className="font-semibold text-foreground mb-2 flex items-center gap-2">
        <FileText className="w-3 h-3" />
        {t('bookingDetailsTable.tilknyttedeBookinger', { defaultValue: 'Tilknyttede bookinger ({{n}})', n: bookings.length })}
      </div>
      {bookings.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] border rounded">
            <thead className="bg-muted border-b">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold">{t('bookingDetailsTable.booking', 'Booking')}</th>
                <th className="px-2 py-1.5 text-left font-semibold">{t('bookingDetailsTable.bedrift', 'Bedrift')}</th>
                <th className="px-2 py-1.5 text-left font-semibold">{t('bookingDetailsTable.fra', 'Fra')}</th>
                {Array.from({ length: stopCount }, (_: any, i: number) => (
                  <th key={`st-${i}`} className="px-2 py-1.5 text-left font-semibold" data-testid={`weekly-bookings-col-st${i + 1}`}>St{i + 1}</th>
                ))}
                <th className="px-2 py-1.5 text-left font-semibold">{t('bookingDetailsTable.til', 'Til')}</th>
                <th className="px-2 py-1.5 text-left font-semibold">{t('bookingDetailsTable.tid', 'Tid')}</th>
                {Array.from({ length: stopCount }, (_: any, i: number) => (
                  <th key={`t-${i}`} className="px-2 py-1.5 text-left font-semibold" data-testid={`weekly-bookings-col-t${i + 1}`}>T{i + 1}</th>
                ))}
                <th className="px-2 py-1.5 text-center font-semibold">{t('bookingDetailsTable.pax', 'Pax')}</th>
                <th className="px-2 py-1.5 text-center font-semibold" title={t('weeklyDateView.typeTitle', 'Type som i Booking-listen: T taxi · MB minibuss · B buss · S = delt · P per passasjer')} data-testid="weekly-bookings-col-type">{t('bookingDetailsTable.type', 'Type')}</th>
                <th className="px-2 py-1.5 text-left font-semibold">{t('bookingDetailsTable.arrival', 'Arrival')}</th>
                <th className="px-2 py-1.5 text-right font-semibold">{t('bookingDetailsTable.pris', 'Pris')}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking: any, bi: number) => {
                const { stops } = aligned[bi];
                const type = getTypeCode(booking);
                const bn = booking.booking_number;
                return (
                <tr key={booking.id || bn} className="border-b hover:bg-background" data-testid={`weekly-booking-row-${bn}`}>
                  <td className="px-2 py-1.5">
                    <span className="font-mono font-medium text-blue-600">{bn}</span>
                  </td>
                  <td className="px-2 py-1.5" title={booking.company_name || booking.customer_name}>
                    {getCompanyShortName(booking.company_name || booking.customer_name) || '—'}
                  </td>
                  <td className="px-2 py-1.5">
                    <LocationCell location={booking.from_location} getLocationColor={getLocationColor} getShortName={getLocationShortName} borderWidth="2px" />
                  </td>
                  {Array.from({ length: stopCount }, (_: any, i: number) => (
                    <td key={`st-${i}`} className="px-2 py-1.5" data-testid={`trip-booking-st${i + 1}-${bn}`}>
                      <LocationCell location={stops[i]?.location} getLocationColor={getLocationColor} getShortName={getLocationShortName} borderWidth="2px" />
                    </td>
                  ))}
                  <td className="px-2 py-1.5">
                    <LocationCell location={booking.to_location} getLocationColor={getLocationColor} getShortName={getLocationShortName} borderWidth="2px" />
                  </td>
                  <td className="px-2 py-1.5 font-mono" data-testid={`trip-booking-tid-${bn}`}>{booking.departure_time || '—'}</td>
                  {Array.from({ length: stopCount }, (_: any, i: number) => (
                    <td key={`t-${i}`} className="px-2 py-1.5 font-mono text-muted-foreground" data-testid={`trip-booking-t${i + 1}-${bn}`}>
                      {stops[i]?.time || '—'}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center">
                    <Badge variant="secondary" className="text-[9px]">
                      {booking.number_of_passengers || booking.passenger_count || 0}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 text-center font-bold tracking-wide" title={type.label} data-testid={`trip-booking-type-${bn}`} data-type-code={type.code}>
                    {type.code}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-muted-foreground" data-testid={`trip-booking-arrival-${bn}`}>{booking.arrival_time || '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {booking.price || booking.calculated_price
                      ? `${(booking.price || booking.calculated_price).toLocaleString()} kr`
                      : '—'}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted-foreground italic">{t('bookingDetailsTable.noBookingsAssociatedWithThis', 'No bookings associated with this trip')}</p>
      )}
    </div>
  );
}
